import React, { FC } from 'react';
import { Checkbox, Input, Dropdown } from 'semantic-ui-react';
import { VOICE_MODELS, VOICE_PROVIDERS, MinimaxVoiceSettings } from '../../../../core/api/VoiceConfig';
import { SettingsContainer, SettingRow, Label, SliderContainer, ValueDisplay } from './styles';

export interface Props {
  settings: MinimaxVoiceSettings;
  onChange: (settings: MinimaxVoiceSettings) => void;
}

export const MinimaxSettings: FC<Props> = ({ settings, onChange }) => {
  const handleChange = (key: keyof MinimaxVoiceSettings, value: any) => {
    onChange({
      ...settings,
      [key]: value
    });
  };

  const handleOptionChange = (key: keyof MinimaxVoiceSettings["options"], value: any) => {
    onChange({
      ...settings,
      options: {
        ...settings.options,
        [key]: value
      }
    });
  };

  const modelOptions = VOICE_MODELS[VOICE_PROVIDERS.MINIMAX].map(model => ({
    key: model.id,
    value: model.id,
    text: model.name
  }));

  const selectedModel = VOICE_MODELS[VOICE_PROVIDERS.MINIMAX].find(m => m.id === settings.model)
    ? settings.model
    : 'speech-2.8-turbo';

  return (
    <SettingsContainer>
      <SettingRow>
        <Label>Model</Label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 1 }}>
            <Dropdown
              selection
              fluid
              options={modelOptions}
              value={selectedModel}
              onChange={(_, { value }) => handleChange('model', value as string)}
            />
          </div>
        </div>
      </SettingRow>

      <SettingRow>
        <SliderContainer>
          <Label>Speed</Label>
          <Input
            type="range"
            min={0.5}
            max={2}
            step={0.1}
            value={settings.speed}
            onChange={(e) => handleChange('speed', parseFloat(e.target.value))}
          />
          <ValueDisplay>{settings.speed.toFixed(1)}</ValueDisplay>
        </SliderContainer>
      </SettingRow>

      <SettingRow>
        <SliderContainer>
          <Label>Pitch</Label>
          <Input
            type="range"
            min={-12}
            max={12}
            step={1}
            value={settings.options.pitch ?? 0}
            onChange={(e) => handleOptionChange('pitch', parseInt(e.target.value, 10))}
          />
          <ValueDisplay>{settings.options.pitch ?? 0}</ValueDisplay>
        </SliderContainer>
      </SettingRow>

      <SettingRow>
        <Label>Text Normalization</Label>
        <Checkbox
          toggle
          checked={settings.options.text_normalization ?? false}
          onChange={(_, { checked }) => handleOptionChange('text_normalization', checked ?? false)}
        />
      </SettingRow>

      <SettingRow>
        <Label>Language Boost</Label>
        <Input
          fluid
          placeholder="e.g. auto, English, Spanish"
          value={settings.options.language_boost ?? ''}
          onChange={(e) => handleOptionChange('language_boost', e.target.value || undefined)}
        />
      </SettingRow>
    </SettingsContainer>
  );
};
